import React, { useMemo, useState } from "react";
import {
  FaEdit,
  FaArrowLeft,
  FaArrowRight,
  FaTrash,
  FaEye,
} from "react-icons/fa";
import {
  getSeenStatus,
  shouldClearSeenOnEyeClick,
} from "../../../utils/validation";
import { formatAssignedPersonnelList } from "../../../utils/personnelDisplay";
import { childHasFirstAllocation } from "../utils/inventoryUnitFilter";
import { ClickableInventoryItemName } from "../../../components/ClickableInventoryItemName";

/**
 * Mobile Inventory Card Component
 *
 * Optimized for mobile view with:
 * - React.memo to prevent unnecessary re-renders
 * - useMemo for computed values (assignedTo, seenStatus, etc.)
 * - Only renders when isMobile is true (no desktop performance impact)
 * - Clean separation of concerns
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - Inventory item with pre-calculated values
 * @param {Object} props.seenSettings - Settings for seen status calculation
 * @param {boolean} props.isChildUser - Whether user is a child user
 * @param {string|null} props.childUserPersonalNumber - Child מ.א. for unit-wide note edit rules
 * @param {string} props.userPermissions - User permissions level
 * @param {Function} props.handleEdit - Edit item handler
 * @param {Function} props.handleReturn - Return item handler
 * @param {Function} props.handleMakeAvailable - Make available handler
 * @param {Function} props.handleDelete - Delete item handler
 * @param {Function} props.handleConfirmSeen - Confirm seen handler
 */
const MobileInventoryCard = React.memo(
  ({
    item,
    hideGroupedColumns = false,
    seenSettings,
    isChildUser,
    childUserPersonalNumber = null,
    userPermissions,
    handleEdit,
    handleReturn,
    handleMakeAvailable,
    handleDelete,
    handleConfirmSeen,
    resolveAssignedByName,
    onItemNameClick,
    onAssignedNameClick,
    onUpdateNote,
    nicknameByPersonalNumber = new Map(),
    personnel = null,
    personnelUnitMap = null,
    units = null,
  }) => {
    // Extract pre-calculated values (no calculations in render)
    const {
      assignedQuantity,
      availableQuantity,
      calculatedStatus,
      assignedPersonnel,
    } = item;

    // Memoize computed values to avoid recalculation on every render
    const itemData = useMemo(() => {
      const isGroup = item.isSerialGroupRow === true;
      const isSerial =
        !isGroup && item.serialNumber && item.serialNumber.trim() !== "";

      const assignedTo = isSerial
        ? assignedPersonnel.length > 0
          ? (() => {
              const name = formatAssignedPersonnelList(
                assignedPersonnel,
                nicknameByPersonalNumber,
                personnel,
              ) || "-";
              const pn = assignedPersonnel[0]?.personalNumber;
              const unitId = pn && personnelUnitMap ? personnelUnitMap.get(pn) : null;
              const unit = unitId && units ? units.find((u) => u.id === unitId) : null;
              const unitName = unit?.unitName || "";
              return unitName ? `${name} (${unitName})` : name;
            })()
          : "-"
        : "";

      const seenStatus = isSerial
        ? getSeenStatus(
            item.seenAt,
            seenSettings.greenMax,
            seenSettings.yellowDays
          )
        : null;

      const seenBtnClears =
        isSerial &&
        shouldClearSeenOnEyeClick(
          item.seenAt,
          seenSettings.greenMax,
          seenSettings.yellowDays
        );

      const statusLabel =
        calculatedStatus === "available"
          ? "זמין"
          : calculatedStatus === "assigned"
          ? "מוחתם"
          : "זוכה";

      const isReturnDisabled =
        item.serialNumber &&
        (calculatedStatus === "assigned" || item.status === "assigned");

      const returnButtonTitle = isReturnDisabled
        ? "לא ניתן לזכות פריט שחתום"
        : item.serialNumber
        ? "זיכוי"
        : "זיכוי (בחר כמות)";

      return {
        isGroup,
        isSerial,
        assignedTo,
        seenStatus,
        seenBtnClears,
        statusLabel,
        isReturnDisabled,
        returnButtonTitle,
      };
    }, [
      item.isSerialGroupRow,
      item.serialGroupStatusSummary,
      item.serialNumber,
      item.seenAt,
      item.status,
      assignedPersonnel,
      calculatedStatus,
      seenSettings.greenMax,
      seenSettings.yellowDays,
      nicknameByPersonalNumber,
      personnel,
      personnelUnitMap,
      units,
    ]);

    const [editingNote, setEditingNote] = useState(false);
    const [noteEditValue, setNoteEditValue] = useState("");

    return (
      <div
        className={`inventory-mobile-card${
          itemData.isGroup ? " inventory-mobile-card--serial-group" : ""
        }`}
      >
        {/* Card Header */}
        <div className="mobile-card-header">
          <h3 className="mobile-card-title">
            <ClickableInventoryItemName
              item={item}
              onItemNameClick={onItemNameClick}
            />
          </h3>
          <div className="mobile-card-header-badges">
            <span
              className={`item-type ${
                item.isOrganic ? "organic" : "synthetic"
              }`}
            >
              {item.isOrganic ? "אורגני" : "קווי"}
            </span>
            {!hideGroupedColumns && (
              <span className={`status-badge ${calculatedStatus}`}>
                {itemData.statusLabel}
              </span>
            )}
          </div>
        </div>

        {/* Card Content */}
        <div className="mobile-card-content">
          {(item.serialNumber || itemData.isGroup) && (
            <div className="mobile-card-row">
              <span className="mobile-card-label">
                {itemData.isGroup ? "סטטוס:" : "מספר סידורי:"}
              </span>
              <span className="mobile-card-value mobile-card-value-block">
                {itemData.isGroup && item.serialGroupSerialCount != null ? (
                  <>
                    <span>{`${item.serialGroupSerialCount} סידוריים`}</span>
                    {item.serialGroupStatusSummary ? (
                      <span className="serial-group-status-summary mobile-serial-group-summary">
                        {item.serialGroupStatusSummary}
                      </span>
                    ) : null}
                  </>
                ) : (
                  item.serialNumber
                )}
              </span>
            </div>
          )}

          {/* Quantity Section - Compact Layout: Titles in one row, values below */}
          {/* Hide quantity for serial items (always 1, status shown by icons) */}
          {(!itemData.isSerial || itemData.isGroup) && (
            <div className="mobile-quantity-section">
              <div className="mobile-quantity-titles">
                <span className="mobile-quantity-title">כמות כללית</span>
                <span className="mobile-quantity-title">כמות מוקצית</span>
                <span className="mobile-quantity-title">כמות זמינה</span>
              </div>
              <div className="mobile-quantity-values">
                <span className="mobile-quantity-value">
                  {item.displayQuantity ?? item.quantity}
                </span>
                <span className="mobile-quantity-value">
                  {assignedQuantity}
                </span>
                <span className="mobile-quantity-value">
                  {availableQuantity}
                </span>
              </div>
            </div>
          )}

          {!hideGroupedColumns &&
            itemData.assignedTo &&
            calculatedStatus !== "available" && (
            <div className="mobile-card-row">
              <span className="mobile-card-label">חתום ל:</span>
              {itemData.isSerial &&
              calculatedStatus === "assigned" &&
              assignedPersonnel.length > 0 ? (
                <span
                  className="clickable-assigned-name"
                  onClick={() => onAssignedNameClick?.(item)}
                >
                  {itemData.assignedTo}
                </span>
              ) : (
                <span className="mobile-card-value">
                  {itemData.assignedTo}
                </span>
              )}
            </div>
          )}

          {!hideGroupedColumns &&
            !isChildUser &&
            assignedPersonnel.length > 0 &&
            calculatedStatus === "assigned" && (
              <div className="mobile-card-row">
                <span className="mobile-card-label">הוקצה על ידי:</span>
                <span className="mobile-card-value">
                  {resolveAssignedByName?.(assignedPersonnel[0].assignedBy) || "-"}
                </span>
              </div>
            )}

          {(() => {
            if (hideGroupedColumns && itemData.isGroup) {
              return null;
            }
            if (itemData.isGroup) {
              return (
                <div className="mobile-card-row">
                  <span className="mobile-card-label">מיקום/הערה:</span>
                  <span className="mobile-card-value">—</span>
                </div>
              );
            }
            let note = assignedPersonnel[0]?.note || "";
            let isChildNote = false;
            if (!isChildUser && !note) {
              const isSerialRow =
                item.serialNumber && item.serialNumber.trim() !== "";
              if (isSerialRow) {
                const secondAssignments = item.assignments?.second || {};
                const firstChildEntry = Object.values(secondAssignments)[0];
                if (firstChildEntry?.note) {
                  note = firstChildEntry.note;
                  isChildNote = true;
                }
              }
            }
            const isUnitPeerNoteReadonly =
              isChildUser &&
              childUserPersonalNumber &&
              !childHasFirstAllocation(item, childUserPersonalNumber);
            const noteReadonly = isChildNote || isUnitPeerNoteReadonly;
            return (
              <div className="mobile-card-row">
                <span className="mobile-card-label">מיקום/הערה:</span>
                {editingNote && !noteReadonly ? (
                  <textarea
                    className="mobile-note-textarea"
                    autoFocus
                    value={noteEditValue}
                    onFocus={(e) => {
                      const len = e.target.value.length;
                      e.target.setSelectionRange(len, len);
                    }}
                    onChange={(e) => setNoteEditValue(e.target.value)}
                    onBlur={() => {
                      if (onUpdateNote && noteEditValue !== (assignedPersonnel[0]?.note || "")) {
                        onUpdateNote(
                          item.id,
                          assignedPersonnel[0]?.personalNumber,
                          noteEditValue
                        );
                      }
                      setEditingNote(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setEditingNote(false);
                    }}
                  />
                ) : (
                  <span
                    className={`mobile-note-text${noteReadonly ? " note-child-readonly" : ""}${!note && !noteReadonly ? " note-cell-empty" : ""}`}
                    title={
                      isChildNote
                        ? note || "הערת חייל (לקריאה בלבד)"
                        : isUnitPeerNoteReadonly
                          ? note || "לקריאה בלבד"
                          : note || "לחץ לעריכה"
                    }
                    onClick={() => {
                      if (!noteReadonly) {
                        setEditingNote(true);
                        setNoteEditValue(note);
                      }
                    }}
                  >
                    {note || "+"}
                  </span>
                )}
              </div>
            );
          })()}

          {!hideGroupedColumns && itemData.isSerial && (
            <div className="mobile-card-row">
              <span className="mobile-card-label">דוח צ:</span>
              <span
                className={`seen-cell ${itemData.seenStatus?.colorClass || ""}`}
              >
                {itemData.seenStatus?.days != null &&
                itemData.seenStatus?.days !== undefined
                  ? itemData.seenStatus.days
                  : "?"}
              </span>
            </div>
          )}
        </div>

        {/* Card Actions */}
        {!hideGroupedColumns && (
        <div className="mobile-card-actions">
          {!itemData.isGroup && itemData.isSerial && (
            <button
              className="seen-btn mobile-action-btn"
              onClick={() => handleConfirmSeen(item.id, item.seenAt)}
              title={
                itemData.seenBtnClears
                  ? "בטל סימון נראה (בטעות)"
                  : "דווח שנראה"
              }
              aria-label={
                itemData.seenBtnClears ? "בטל סימון נראה" : "דווח שנראה"
              }
            >
              <FaEye />
            </button>
          )}

          {!itemData.isGroup && !isChildUser && (
            <button
              className="edit-btn mobile-action-btn"
              onClick={() => handleEdit(item)}
              title="ערוך"
              aria-label="ערוך פריט"
            >
              <FaEdit />
            </button>
          )}

          {!itemData.isGroup &&
            !isChildUser &&
            (calculatedStatus === "returned" ? (
              <button
                className="available-btn mobile-action-btn"
                onClick={() => handleMakeAvailable(item.id)}
                title="החזר לזמין"
                aria-label="החזר לזמין"
              >
                <FaArrowRight />
              </button>
            ) : (
              <button
                className="return-btn mobile-action-btn"
                onClick={() => handleReturn(item.id)}
                disabled={itemData.isReturnDisabled}
                title={itemData.returnButtonTitle}
                aria-label={itemData.returnButtonTitle}
              >
                <FaArrowLeft />
              </button>
            ))}

          {!itemData.isGroup && userPermissions === "admin" && (
            <button
              className="delete-btn mobile-action-btn"
              onClick={() => handleDelete(item.id)}
              title="מחק"
              aria-label="מחק פריט"
            >
              <FaTrash />
            </button>
          )}
        </div>
        )}
      </div>
    );
  }
);

MobileInventoryCard.displayName = "MobileInventoryCard";

export default MobileInventoryCard;
